import { z } from "zod";

import { compareDecimalStrings, PERCENTAGE_INPUT_PATTERN, trimTrailingZeros } from "@/shared/utils/decimal";

import type { CreatePaymentTaxPayload, PaymentTax } from "../../types/paymentTaxes.types";
import type { TaxScopeChoice } from "../../utils/taxCoverage";
import { toIsoInstant } from "../fees/corridorPriceFormSchema";
import { MAX_GROSS_AMOUNT_RATE } from "./taxFormSchema";

export interface CorridorTaxFormMessages {
  rate: string;
  rateGross: string;
  validFrom: string;
  reason: string;
  acknowledge: string;
}

export function buildCorridorTaxSchema(messages: CorridorTaxFormMessages) {
  return z
    .object({
      code: z.string().min(1),
      base: z.enum(["COMMISSION", "GROSS_AMOUNT"]),
      target: z.enum(["BROAD", "CORRIDOR"]),
      exempt: z.boolean(),
      rate: z.string(),
      bearer: z.enum(["CLIENT", "COLOCA"]),
      effectiveNow: z.boolean(),
      validFromDate: z.string(),
      validFromTime: z.string(),
      reason: z.string().max(500),
      requiresAck: z.boolean(),
      acknowledged: z.boolean(),
    })
    .superRefine((values, ctx) => {
      if (!values.exempt) {
        const rate = values.rate.trim();
        if (!PERCENTAGE_INPUT_PATTERN.test(rate)) {
          ctx.addIssue({ code: "custom", path: ["rate"], message: messages.rate });
        } else if (values.base === "GROSS_AMOUNT" && compareDecimalStrings(rate, MAX_GROSS_AMOUNT_RATE) > 0) {
          ctx.addIssue({ code: "custom", path: ["rate"], message: messages.rateGross });
        }
      }
      if (!values.effectiveNow && !values.validFromDate) {
        ctx.addIssue({ code: "custom", path: ["validFromDate"], message: messages.validFrom });
      }
      if (values.requiresAck) {
        if (!values.reason.trim()) {
          ctx.addIssue({ code: "custom", path: ["reason"], message: messages.reason });
        }
        if (!values.acknowledged) {
          ctx.addIssue({ code: "custom", path: ["acknowledged"], message: messages.acknowledge });
        }
      }
    });
}

export type CorridorTaxFormValues = z.input<ReturnType<typeof buildCorridorTaxSchema>>;

export const CORRIDOR_TAX_STEP_FIELDS = [
  "rate",
  "validFromDate",
] as const satisfies readonly (keyof CorridorTaxFormValues)[];

/** The rate and bearer a code starts from: what applies on the corridor today, or the rule's own. */
export function seedCorridorTax(
  code: string,
  start: PaymentTax | null,
  template: PaymentTax | null,
  target: CorridorTaxFormValues["target"],
  requiresAck: boolean,
): CorridorTaxFormValues {
  const source = start ?? template;
  const exempt = source != null && compareDecimalStrings(source.rate, "0") === 0;
  return {
    code,
    base: source?.base ?? "COMMISSION",
    target,
    exempt,
    rate: source && !exempt ? trimTrailingZeros(source.rate) : "",
    bearer: source?.bearer ?? "CLIENT",
    effectiveNow: true,
    validFromDate: "",
    validFromTime: "09:00",
    reason: "",
    requiresAck,
    acknowledged: false,
  };
}

export function validFromOf(values: CorridorTaxFormValues): string | null {
  return values.effectiveNow ? null : toIsoInstant(values.validFromDate, values.validFromTime);
}

export function corridorTaxPayload(
  values: CorridorTaxFormValues,
  template: PaymentTax,
  scope: TaxScopeChoice,
  organizationId: string | null,
): CreatePaymentTaxPayload {
  const validFrom = validFromOf(values);
  const reason = values.reason.trim();
  return {
    code: template.code,
    name: template.name,
    country: template.country,
    base: template.base,
    bearer: values.bearer,
    rate: values.exempt ? "0" : values.rate.trim(),
    ...(organizationId ? { organization_id: organizationId } : {}),
    ...(scope.rail ? { rail: scope.rail } : {}),
    ...(scope.direction ? { direction: scope.direction } : {}),
    ...(validFrom ? { valid_from: validFrom } : {}),
    ...(reason ? { change_reason: reason } : {}),
  };
}
